import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { Box, Paper, Typography, Alert, CircularProgress, IconButton, Drawer, ClickAwayListener } from '@mui/material'
import axios from 'axios'
import { downloadSpecXlsx, openSavedTableXlsx } from '../utils/tableXlsxDownload'
import { CarbonSpec } from '../types/carbon/models/CarbonSpec'
import TableChartIcon from '@mui/icons-material/TableChart'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import useAccount from '../hooks/useAccount'
import useMultiTable from '../hooks/useMultiTable'
import useSnackbar from '../hooks/useSnackbar'
import useRouter from '../hooks/useRouter'
import usePersonas from '../hooks/usePersonas'
import useConfig from '../hooks/useConfig'
import { entityKey, tableConfigEntity } from '../utils/configEntity'
import { configApi } from '../services/configApi'
import { ConfigType, MergedConfigResult } from '../types/config'
import { GenNode } from '../types/carbon/models/GenNode'
import { getNodeID, getNodeTitle } from '../utils/nodeTypes'
import Tree from '../components/tree/Tree'
import { flattenTree, getInitialExpandedIds } from '../utils/tree'
import { PlatinumData } from '../types/carbon/models/PlatinumData'
import ConfigEditor from '../components/config/ConfigEditor'
import PageAgentPane from '../agent/components/PageAgentPane'
import { TABLES_PANE_PROFILE } from '../agent/pageHelpers'
import { shouldCloseOnClickAway } from '../agent/components/paneClickAway'
import useTableStarter from '../hooks/useTableStarter'
import usePageAgentHost from '../hooks/usePageAgentHost'
import { INSIGHTS_DRAWER_WIDTH } from '../components/chat/ChatDrawer'
import { refuse } from '../utils/permissionReason'
import useAiAccess from '../hooks/useAiAccess'
import useWhoToAsk from '../hooks/useWhoToAsk'
import useTablePath from '../hooks/useTablePath'
import useAutoCoderTable from '../hooks/useAutoCoderTable'
import useSpecEditor from '../hooks/useSpecEditor'
import SideBarPage from '../components/system/SidebarPage'
import MultiTableHeader from '../components/multi-table/MultiTableHeader'
import { MultiTableAnalysisDrawer } from '../components/multi-table-analysis'
import {
  API_BASE_URL,
} from '../constants/system'
import PageHeader from '../components/system/PageHeader'
import MenuActionsButton from '../components/system/MenuActionsButton'
import DeleteConfirmWindow from '../components/system/DeleteConfirmWindow'
import useTableRemoval from '../hooks/useTableRemoval'
import { TocSelectToggle, TocSelectBar, TableDeleteProgress } from '../components/tables/TocSelect'
import {
  MultiTableConfigTable,
  TableVisibilitySettings,
} from '../types/gotypes'
import {
  MULTI_TABLE_ID_META_KEY,
  CBT_SIDE_META_KEY,
  normalizeTablePath,
} from '../utils/multitables'
import {
  getURLBasedVisibilitySettings,
  getDefaultTableViewSettings,
  convertVisibilitySettingsToURL,
  getAllVisibilityParamNames,
  getAllTableSettingParamNames,
  extractUserFolder,
} from '../utils/tables'
import PlatinumTableWrapper, { PlatinumTableWrapperHandle } from '../components/platinum-table-new/PlatinumTableWrapper'
import FilterEditorModal from '../components/spec-editor/FilterEditorModal'
import SpecSettingsDrawer from '../components/spec-editor/SpecSettingsDrawer'
import { clearFolderCache, rebuildFolderCache } from '../services/admin/settingsApi'
import FolderCacheResultDialog from '../components/admin/FolderCacheResultDialog'
import type { ClearFolderCacheResponse, RebuildFolderCacheResponse } from '../types/admin/settings'
import SaveTableDialog from '../components/tables/SaveTableDialog'
import useRecentTables from '../hooks/useRecentTables'
import useUserTOC from '../hooks/useUserTOC'
import { tocApi } from '../services/tocApi'
import FolderPickerDialog, { collectUserFolders } from '../components/batch/FolderPickerDialog'
import { buildTablesVoiceTools, TablesVoiceToolDeps } from '../voice/tablesVoiceTools'
import { useVoiceRegistration } from '../voice/useVoiceRegistration'
import { createUIBridge, UIBridge } from '../voice/uiBridge'
import { VoiceUITool } from '../voice/types'
import { blobErrorMessage } from '../utils/blobErrorMessage'
import { buildTocNodeActions, execFolderPath, isColleagueRoot, tocActionRefusal, TocActionContext, TocActionKey } from '../components/tables/tocNodeActions'
import TocNameDialog from '../components/tables/TocNameDialog'
import { TablesHeaderActions, TablesPreviewBanner } from '../components/tables/TablesPageActions'
import { PlatinumProperties2 } from '../types/carbon/models/PlatinumProperties2'
import { XSpecProperties } from '../types/carbon/models/XSpecProperties'
import { extractErrorMessage } from '../utils/apitools'
import { withAndedFilter } from '../utils/filter'

/** Extract the exec folder path from a table's full blob path */
function getExecFolderFromPath(fullPath: string): string {
  // fullPath is like "Tables/Exec/Folder/Sub/TableName" (no .cbt)
  const parts = fullPath.split('/')
  // Tables/Exec/{folders...}/TableName — folder parts are index 2 to n-1
  if (parts.length <= 3) return '' // root level (Tables/Exec/TableName)
  return parts.slice(2, parts.length - 1).join('/')
}

/** What the page's tools read and call, set from every render (#1534). */
interface TablesLive {
  table: string
  isAutoCoder: boolean
  isPreviewMode: boolean
  hasUnsavedViewSettings: boolean
  tableData: PlatinumData | undefined
  filter: string
  pageContext: () => string
  applyVisibilitySettings: (settings: Record<string, boolean>) => void
  applyChartType: (chartType: string) => void
  handleEdit: () => void
  newTable: () => void
  toggleChat: () => void
  discardChanges: () => void
  saveViewSettings: () => Promise<void> | void
  handleSaveTable: () => void
  handleDownloadXLSX: () => Promise<void>
  star: () => void
  close: () => void
  view: () => PlatinumTableWrapperHandle | null
  validateFilter: (expression: string) => Promise<string | null>
  handleQuickFilterSave: (expression: string) => void
  handleClearFilter: () => void
  openQuickFilter: () => void
  variableTree: () => Promise<GenNode[]>
  variableCodes: (variable: string) => Promise<GenNode[]>
  settingsOpen: boolean
  displayProperties: PlatinumProperties2
  specProperties: XSpecProperties
  handleOpenSettings: () => void
  writeSettings: (displayProperties: PlatinumProperties2, specProperties: XSpecProperties) => void
  handleApplySettings: () => void
  handleCloseSettings: () => void
  tocNodes: GenNode[]
  tocActionsFor: (node: GenNode) => TocActionKey[]
  tocRefusalFor: (node: GenNode, key: TocActionKey) => string | undefined
  renameTocItem: (node: GenNode, newName: string) => Promise<string>
  duplicateTocTable: (node: GenNode, newName: string) => Promise<string>
  copyTableToMyTables: (node: GenNode, name: string) => Promise<string>
  createTocFolder: (parent: GenNode, name: string) => Promise<string>
  openTocTable: (tablePath: string) => void
  expandTocNode: (node: GenNode) => void
  handleExportFolderXlsx: (node: GenNode) => Promise<void>
}

export const Tables: React.FC = () => {
  const account = useAccount()
  const snackbar = useSnackbar()
  const router = useRouter()

  const {
    customer,
    job,
    table,
    tableParam,
    tableTitle,
    filter,
    multiTableId,
    isMultiTableRoute,
    tableName,
    folderPath,
  } = useTablePath()

  const userTOC = useUserTOC(customer, job)
  const recentTables = useRecentTables(customer || '', job || '', account.toc)

  // The tree and user.toc, read again together. Server routes (save, delete,
  // move, folder rename) rewrite user.toc, so the copy held here must be read
  // again after each one: the next New Folder or Move up would otherwise
  // write the old copy back and undo the server's change.
  const reloadTree = async () => {
    await Promise.all([account.reloadTOC(), userTOC.load()])
  }

  // Delete (a table's folder, or the ticked rows in Select mode) and a My
  // Tables folder rename: the server resolves and moves what is inside (#1795).
  const removal = useTableRemoval(customer, job, async (deleted) => {
    recentTables.removeRecent(deleted)
    await reloadTree()
    const open = (table || '').toLowerCase()
    if (open && deleted.some(p => p.toLowerCase().replace(/\.cbt$/, '') === open)) {
      account.openPage('tables', { table: '' })
    }
  })

  const multiTable = useMultiTable({
    customer,
    job,
    multiTableId,
    table,
  })

  // Compute effective filter for multi-table mode: prefer URL filter, fall back to saved config filter
  // This eliminates the race condition where router.setParams is async but rendering is sync
  const effectiveFilter = useMemo(() => {
    if (!isMultiTableRoute) return filter
    // In multi-table mode, prefer URL filter if set, otherwise use saved config filter
    return filter || multiTable.multiTable?.config?.global_filter || ''
  }, [isMultiTableRoute, filter, multiTable.multiTable?.config?.global_filter])

  const urlParams = new URLSearchParams(window.location.search)

  const [expanded, setExpanded] = useState<string[]>([])
  const [helperOpen, setHelperOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<{ type: 'table' | 'multitable', data: any } | null>(null)
  const [moveDialogOpen, setMoveDialogOpen] = useState(false)
  const [pendingMoveNode, setPendingMoveNode] = useState<GenNode | null>(null)
  const [jobMergedConfig, setJobMergedConfig] = useState<MergedConfigResult | null>(null)
  const [analysisDrawerOpen, setAnalysisDrawerOpen] = useState(false)
  const [folderCacheResult, setFolderCacheResult] = useState<ClearFolderCacheResponse | RebuildFolderCacheResponse | null>(null)
  const [folderCacheDialogOpen, setFolderCacheDialogOpen] = useState(false)
  const [folderCacheMode, setFolderCacheMode] = useState<'clear' | 'rebuild'>('clear')
  const [quickFilterOpen, setQuickFilterOpen] = useState(false)
  const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false)
  const settingsSnapshotRef = useRef<string>('')
  const tableWrapperRef = useRef<PlatinumTableWrapperHandle>(null)
  const [hasUnsavedViewSettings, setHasUnsavedViewSettings] = useState(false)

  // Config hook (for settings modal)
  const {
    configEditorOpen,
    configEntity,
    mergedConfig,
    loadingConfig,
    savingConfig,
    handleNodeSettings,
    handleSaveConfig,
    openConfigEntity,
    closeConfigEditor
  } = useConfig(customer, job)

  // AutoCoder hook for URL-based table loading
  const autoCoderTable = useAutoCoderTable({
    customer: customer || '',
    job: job || '',
  })

  // Quick Settings' Context is the open table's own, keyed by its path the way
  // Insights reads it. Its own useConfig, so it never saves to whatever the
  // tree's Context last opened, and that editor keeps its own target. None for
  // a table with no folder (new, AutoCoder): Insights reads the job's there.
  const quickSettingsConfig = useConfig(customer, job)
  const openTableContext = autoCoderTable.isAutoCoderMode ? null : tableConfigEntity(customer, job, tableParam)
  const quickSettingsEntity = quickSettingsConfig.configEntity
  const openTableContextLoaded = !!openTableContext && !!quickSettingsEntity && entityKey(quickSettingsEntity) === entityKey(openTableContext)

  // State to receive loaded table data from PlatinumTableWrapper
  const [effectivePlatinumData, setEffectivePlatinumData] = useState<PlatinumData | undefined>(undefined)
  // A table switch keeps the old table's data until the new one loads: drop it
  // at once, so nothing (the Insights summary, read_open_table) takes the old
  // table for the new one. In render, not an effect: the table wrapper hands
  // over a cached table's data in its own effect, which runs before ours.
  const [dataTableParam, setDataTableParam] = useState(tableParam)
  if (dataTableParam !== tableParam) {
    setDataTableParam(tableParam)
    setEffectivePlatinumData(undefined)
  }

  // Check if a table path belongs to the current user (used for showing delete option)
  const checkIsUserTable = (path: string) => {
    if (!account.user) return false
    const userEmail = account.user.session_info?.email || ''
    return path.toLowerCase().startsWith(`tables/user/${userEmail.toLowerCase()}`)
  }

  // Check if a table path belongs to a colleague (read-only)
  const checkIsColleagueTable = (path: string) => {
    if (!account.user) return false
    const lower = path.toLowerCase()
    if (!lower.startsWith('tables/user/')) return false
    const userEmail = account.user.session_info?.email || ''
    return !lower.startsWith(`tables/user/${userEmail.toLowerCase()}`)
  }

  const specEditor = useSpecEditor({
    customer,
    job,
    table: tableParam,
    skipSyntax: true,
    // when we clear the preview we need to reset the table data
    onClearPreview: () => {
      setEffectivePlatinumData(undefined)
    },
    onSavedSpec: async () => {
      await reloadTree()
      setExpanded([])
    },
  })

  // Helper to clear draft for a specific table path
  const clearDraftForTable = (tablePath: string) => {
    if (!customer || !job) return
    const base = `${customer}/${job}/${(tablePath || 'new').replace(/\.cbt$/, '')}`
    const draftKey = `editdraft:${base}`
    try {
      window.sessionStorage.removeItem(draftKey)
    } catch (err) {
      console.warn('Failed to remove draft from sessionStorage:', err)
    }
  }

  // Determine if we're in spec preview mode:
  // hasPreviewSpec = CarbonSpec saved by EditSpec to sessionStorage
  // hasPreviewData = PlatinumData from running the spec
  // needsRun = have spec but no data yet, or data that ran under another
  // Quick Filter (a URL opened with a new ?filter=), so POST /run
  const hasPreviewSpec = !autoCoderTable.isAutoCoderMode && Boolean(specEditor.previewSpec)
  const hasPreviewData = !autoCoderTable.isAutoCoderMode && Boolean(specEditor.previewData)
  const isPreviewMode = hasPreviewSpec
  const previewRanFilter = (() => {
    try { return window.sessionStorage.getItem(specEditor.sessionStorageKeys.dataFilter) ?? '' } catch { return effectiveFilter }
  })()
  const needsRun = hasPreviewSpec && (!hasPreviewData || previewRanFilter !== effectiveFilter)

  // Track whether we're currently running a spec
  const [specRunning, setSpecRunning] = useState(false)
  const [specRunError, setSpecRunError] = useState<string | null>(null)
  const specRunRef = useRef(0)

  // Run spec on-demand when we have a spec but no data
  useEffect(() => {
    if (!needsRun || specRunning) return
    if (!customer || !job || !specEditor.previewSpec) return

    const runId = ++specRunRef.current
    const runSpec = async () => {
      setSpecRunning(true)
      setSpecRunError(null)
      try {
        // AND the URL quick filter onto the spec's own filter
        const specToRun = withAndedFilter(specEditor.previewSpec!, effectiveFilter)
        const result = await axios.post<PlatinumData>(
          `${API_BASE_URL}/tables/${customer}/${job}/run?format=json`,
          specToRun,
        )
        if (specRunRef.current !== runId) return // stale
        // Store the result in sessionStorage so specEditor.previewData picks it up
        const base = `${customer}/${job}/${(tableParam || 'new').replace(/\.cbt$/, '')}`
        try {
          window.sessionStorage.setItem(`previewdata:${base}`, JSON.stringify(result.data))
          window.sessionStorage.setItem(specEditor.sessionStorageKeys.dataFilter, effectiveFilter)
        } catch {}
        // Trigger reload of preview data from sessionStorage
        specEditor.onLoadPreview?.()
      } catch (error) {
        if (specRunRef.current !== runId) return // stale
        const msg = error instanceof Error ? error.message : 'Unknown error'
        setSpecRunError(msg)
        snackbar.error(`Failed to run spec: ${msg}`)
      } finally {
        if (specRunRef.current === runId) {
          setSpecRunning(false)
        }
      }
    }
    runSpec()
  }, [needsRun, customer, job, specEditor.previewSpec, effectiveFilter])

  // Store a changed preview spec and drop its data, so the temporary table
  // runs again from it (needsRun → the effect above).
  // #1885: the preview spec is then the table's only store of what it shows.
  // Visibility params left in the URL by the saved table would be read back
  // into Quick Settings on its next open and undo this change, so they go.
  const rerunPreviewSpec = (spec: CarbonSpec) => {
    try {
      window.sessionStorage.setItem(specEditor.sessionStorageKeys.spec, JSON.stringify(spec))
      window.sessionStorage.removeItem(specEditor.sessionStorageKeys.data)
    } catch {}
    specEditor.onLoadPreview?.()
    const staleVisibility = getAllVisibilityParamNames().filter(p => router.params[p] !== undefined)
    if (staleVisibility.length > 0) router.removeParams(staleVisibility, { history: 'replace' })
  }

  // Handler for visibility toggles in spec preview mode
  // Updates displayProperties in the stored CarbonSpec and triggers a re-run
  const handleSpecVisibilityOverride = useCallback((newVisibility: Partial<{
    frequency: boolean
    column_percent: boolean
    row_percent: boolean
    significance: boolean
  }>) => {
    if (!specEditor.previewSpec || !customer || !job) return

    const updatedSpec: CarbonSpec = {
      ...specEditor.previewSpec,
      displayProperties: {
        ...specEditor.previewSpec.displayProperties,
        cells: {
          // Defaults
          frequencies: false,
          columnPercents: true,
          rowPercents: false,
          percentSign: false,
          missingAsZero: false,
          missingAsBlank: false,
          zeroAsBlank: false,
          blankAsChar: false,
          percentsAsProportions: false,
          thousandsSeparator: false,
          showRedundant100: false,
          ...specEditor.previewSpec.displayProperties?.cells,
          // Apply visibility overrides
          ...(newVisibility.frequency !== undefined && { frequencies: newVisibility.frequency }),
          ...(newVisibility.column_percent !== undefined && { columnPercents: newVisibility.column_percent }),
          ...(newVisibility.row_percent !== undefined && { rowPercents: newVisibility.row_percent }),
        },
        significance: {
          visible: false,
          type: 'ColumnGroups' as const,
          propStat: 'Z' as const,
          meanStat: 'T' as const,
          autoParam: false,
          letters: 'Column' as const,
          appendLetters: false,
          headcount: 'Weighted' as const,
          meanPoolVariance: false,
          propPooledEst: false,
          continuityCorr: false,
          skipBase30: false,
          skipCell5: false,
          lettersLong: false,
          oneTailed: false,
          hiLoTest: false,
          ...specEditor.previewSpec.displayProperties?.significance,
          ...(newVisibility.significance !== undefined && { visible: newVisibility.significance }),
        },
      },
    }

    rerunPreviewSpec(updatedSpec)
  }, [specEditor.previewSpec, customer, job, tableParam, router])

  // The page's tools (#1534): one registry for the voice assistant and the
  // Insights pane. Every tool reads the page through liveRef, which is set
  // from each render further down, so a tool that waits across renders
  // (Quick Settings: open, change, apply) always sees the latest state, and
  // handlers declared later in this component are reached without a closure
  // the React Compiler would memoise before they exist.
  const liveRef = useRef<TablesLive | null>(null)
  const live = () => {
    if (!liveRef.current) throw new Error('the Tables page is not ready')
    return liveRef.current
  }
  const voiceToolsRef = useRef<VoiceUITool[] | null>(null)
  if (!voiceToolsRef.current) {
    const deps: TablesVoiceToolDeps = {
      applySettings: (settings) => live().applyVisibilitySettings(settings),
      renderChart: (chartType) => live().applyChartType(chartType),
      getPageContext: () => live().pageContext(),
      page: {
        hasTable: () => !!live().table,
        isAutoCoder: () => live().isAutoCoder,
        isPreviewMode: () => live().isPreviewMode,
        hasUnsavedChanges: () => live().hasUnsavedViewSettings,
        tableData: () => live().tableData,
        openEditSpec: () => live().handleEdit(),
        newTable: () => live().newTable(),
        toggleInsights: () => live().toggleChat(),
        discardChanges: () => live().discardChanges(),
        saveViewSettings: () => live().saveViewSettings(),
        saveTable: () => live().handleSaveTable(),
        download: () => { void live().handleDownloadXLSX() },
        star: () => live().star(),
        close: () => live().close(),
      },
      view: () => live().view(),
      filter: {
        current: () => live().filter,
        validate: (expression) => live().validateFilter(expression),
        apply: (expression) => live().handleQuickFilterSave(expression),
        clear: () => live().handleClearFilter(),
        open: () => live().openQuickFilter(),
      },
      variables: {
        tree: () => live().variableTree(),
        codes: (variable) => live().variableCodes(variable),
      },
      quickSettings: {
        isOpen: () => live().settingsOpen,
        open: () => live().handleOpenSettings(),
        read: () => ({ displayProperties: live().displayProperties, specProperties: live().specProperties }),
        write: (dp, sp) => live().writeSettings(dp, sp),
        apply: () => live().handleApplySettings(),
        cancel: () => live().handleCloseSettings(),
      },
      toc: {
        nodes: () => live().tocNodes,
        openTablePath: () => live().table,
        actionsFor: (node) => live().tocActionsFor(node),
        refusalFor: (node, key) => live().tocRefusalFor(node, key),
        rename: (node, name) => live().renameTocItem(node, name),
        duplicate: (node, name) => live().duplicateTocTable(node, name),
        copyToMyTables: (node, name) => live().copyTableToMyTables(node, name),
        createFolder: (parent, name) => live().createTocFolder(parent, name),
        open: (tablePath) => live().openTocTable(tablePath),
        expand: (node) => live().expandTocNode(node),
        downloadFolder: (node) => live().handleExportFolderXlsx(node),
      },
    }
    voiceToolsRef.current = buildTablesVoiceTools(deps)
  }
  const voiceTools = voiceToolsRef.current

  // Insights is the Tables page helper (Douglas plan T9), with this page's
  // tools. The pane opens at every AI level and shows the open table's
  // summary (the server's own AI check covers making one). With agent access
  // it is the full agent (page-tables). At level 2, "Insights pane" (#1884),
  // it is the Tables pane: it reads, filters and finds tables on Sonnet, the
  // server refuses it anything more, and Open in full chat is disabled with
  // why. Below that the composer is disabled with the standard sentence and
  // who to ask.
  const aiAccess = useAiAccess(customer)
  const whoToAsk = useWhoToAsk(customer)
  const fullAgent = aiAccess.levelAllows('agent_sessions')
  // levelAllows answers yes before /me loads; a send then would make a chat
  // of the wrong profile, so the composer waits for the level.
  const helperComposerReason = !aiAccess.loaded
    ? 'Checking your access, one moment.'
    : fullAgent || aiAccess.levelAllows('tables_insights')
      ? undefined
      : refuse("You can't chat with the Tables helper without Insights pane access.", whoToAsk).reason
  const helperProfile = fullAgent ? undefined : TABLES_PANE_PROFILE
  const fullChatReason = fullAgent
    ? undefined
    : refuse("You can't open this in the full agent without agent access.", whoToAsk).reason
  const pageAgentHost = usePageAgentHost(customer || '')

  // The saved table's summary, the pane's first row (no chat is made for it).
  const starter = useTableStarter({
    customer: customer || '',
    job: job || '',
    path: table || '',
    filter: effectiveFilter || '',
    tableData: effectivePlatinumData,
    enabled: helperOpen && !!table && !isPreviewMode && !autoCoderTable.isAutoCoderMode,
    includeSideLabel: jobMergedConfig?.data?.include_side_label ?? true,
  })

  // --- Floating assistant ---
  const isManager = account.user?.role_permissions.isManager

  // applyVisibilitySettings: shared by voice tools.
  // settings keys are the agent/voice schema keys (frequencies, columnPercents, rowPercents, significance).
  const applyVisibilitySettings = useCallback((settings: Record<string, boolean>) => {
    const keyMap: Record<string, string> = {
      frequencies: 'frequency',
      columnPercents: 'column_percent',
      rowPercents: 'row_percent',
      significance: 'significance',
    }
    const visibilityUpdates: Record<string, boolean> = {}
    for (const [agentKey, urlKey] of Object.entries(keyMap)) {
      if (settings[agentKey] !== undefined) {
        visibilityUpdates[urlKey] = settings[agentKey]
      }
    }
    if (Object.keys(visibilityUpdates).length > 0) {
      const current = getURLBasedVisibilitySettings()
      const merged = { ...current, ...visibilityUpdates }
      // #1813: a temporary table never reads the URL's visibility; it runs its
      // preview spec again, as its toolbar does.
      if (isPreviewMode) handleSpecVisibilityOverride(visibilityUpdates)
      const urlParams = isPreviewMode ? {} : convertVisibilitySettingsToURL(merged as any)
      const allVisibilityParams = getAllVisibilityParamNames()
      const currentParams = { ...router.params }
      delete currentParams['view_mode']
      delete currentParams['chart_type']
      allVisibilityParams.forEach(p => { delete currentParams[p] })
      router.navigate(router.name, { ...currentParams, ...urlParams, view_mode: 'table' })
    }
  }, [router, isPreviewMode, handleSpecVisibilityOverride])

  // applyChartType: shared by voice tools.
  const applyChartType = useCallback((chartType: string) => {
    const chartTypeMap: Record<string, string> = {
      bar: 'bar',
      line: 'line',
      pie: 'pie',
      stacked: 'stackedColumn',
    }
    const mapped = chartTypeMap[chartType] || 'bar'
    const allSettingParams = getAllTableSettingParamNames()
    const currentParams = { ...router.params }
    allSettingParams.forEach(p => {
      if (p === 'view_mode' || p === 'chart_type') delete currentParams[p]
    })
    router.navigate(router.name, { ...currentParams, view_mode: 'graph', chart_type: mapped })
  }, [router])

  const assistantPageContext = useCallback(() => {
    const parts: string[] = []
    if (customer) parts.push(`Customer: ${customer}`)
    if (job) parts.push(`Job: ${job}`)
    if (table) parts.push(`Table: ${table}`)
    if (effectiveFilter) parts.push(`Filter: ${effectiveFilter}`)
    // Include current spec so agent can pass it to get_table_data
    if (specEditor.topSyntax || specEditor.sideSyntax) {
      const spec = {
        top: specEditor.topSyntax,
        side: specEditor.sideSyntax,
        filter: specEditor.filter || '',
        weight: specEditor.weight || '',
        useFilter: !!specEditor.filter,
        useWeight: !!specEditor.weight,
      }
      parts.push(`Spec: ${JSON.stringify(spec)}`)
    }
    // Include visibility settings so agent doesn't need a separate tool
    const settings = getURLBasedVisibilitySettings()
    if (Object.keys(settings).length > 0) {
      parts.push(`Visible: ${Object.entries(settings).filter(([,v]) => v).map(([k]) => k).join(', ') || 'defaults'}`)
    }
    return parts.join('\n')
  }, [customer, job, table, effectiveFilter, specEditor.topSyntax, specEditor.sideSyntax, specEditor.filter, specEditor.weight])

  // What the page helper is told with each message: the page context, and where
  // the numbers are. It holds every page tool, so it is not pointed at ui_search.
  const agentPageContext = useCallback(() => {
    const kind = autoCoderTable.isAutoCoderMode ? 'AutoCoder table' : isPreviewMode ? 'unsaved preview' : table ? 'saved table' : 'no table open'
    return [assistantPageContext(), `Open table: ${kind}. Read its numbers with read_open_table.`].join('\n')
  }, [assistantPageContext, autoCoderTable.isAutoCoderMode, isPreviewMode, table])

  // Register this page's voice tools with the app-scoped voice assistant for as
  // long as Tables is mounted. The session itself lives in the provider above
  // the router, so it survives navigation; this only swaps the active toolset.
  useVoiceRegistration({
    pageKey: `tables:${customer}:${job}:${table}`,
    uiTools: voiceTools,
    // The Help pane reaches the page's other tools through ui_search (#1534).
    pageContext: () => `${assistantPageContext()}\nMore tools for this page (Quick Settings, the chart, the table list, renaming and folders): call ui_search.`,
  })

  // The persona picker: the customer's personas library, Company-level then
  // the user's own (#1523) — no longer the job's merged config.
  const personas = usePersonas({ customer: customer || '' }).personaNames

  const { selectedID, cbtSide } = useMemo(() => {
    if (!table) return { selectedID: '', cbtSide: undefined }
    // Normalize path: forward slashes, lowercase, remove .cbt suffix
    const normalizedTable = table.replace(/\\/g, '/').toLowerCase().replace(/\.cbt$/, '')

    // Recursively search tree to find the node and build its tree item ID
    // This mirrors convertNodesToTreeItems exactly to ensure IDs match
    const findNodeTreeItemId = (
      nodes: GenNode[],
      targetPath: string,
      parentId: string = ''
    ): { treeItemId: string; node: GenNode } | null => {
      for (const node of nodes) {
        const baseID = node.id ? node.id.toString() : getNodeID(node)
        const uniqueID = parentId ? `${parentId}/${baseID}` : baseID

        // Normalize node path the same way for comparison
        const nodePathNormalized = getNodeID(node).toLowerCase().replace(/\.cbt$/, '')
        if (nodePathNormalized === targetPath) {
          return { treeItemId: uniqueID, node }
        }

        if (node.children && node.children.length > 0) {
          const result = findNodeTreeItemId(node.children, targetPath, uniqueID)
          if (result) return result
        }
      }
      return null
    }

    const nodes = multiTable.augmentedToc.nodes || []
    const result = findNodeTreeItemId(nodes, normalizedTable)

    const side = result?.node?.type === 'Table'
      ? result.node.meta?.find(m => m.key === CBT_SIDE_META_KEY)?.value
      : undefined

    return {
      selectedID: result?.treeItemId || '',
      cbtSide: side
    }
  }, [multiTable.augmentedToc.nodes, table])

  // Auto-expand tree to show selected table
  useEffect(() => {
    if (!selectedID) return

    // Build ancestor IDs from hierarchical ID (e.g., "1/2/3/4" -> ["1", "1/2", "1/2/3"])
    const parts = selectedID.split('/')
    const ancestorIDs: string[] = []
    for (let i = 1; i < parts.length; i++) {
      ancestorIDs.push(parts.slice(0, i).join('/'))
    }

    if (ancestorIDs.length > 0) {
      setExpanded(prev => {
        const newExpanded = new Set([...prev, ...ancestorIDs])
        return Array.from(newExpanded)
      })
    }
  }, [selectedID])

  const toggleChat = () => setHelperOpen(open => !open)
  // The pane stays mounted once opened, so closing keeps its chat for a
  // reopen on the same table (resumeKey).
  const [helperMounted, setHelperMounted] = useState(false)
  if (helperOpen && !helperMounted) setHelperMounted(true)

  const onNodeSelectToggle = (itemId: string, node: GenNode, isSelected: boolean) => {
    if(!isSelected) return
    if(node.type !== 'Table' && node.type !== 'MultiTable') return

    if (node.type === 'Table') {
      const fullPath = getNodeID(node)
      // Track in recent tables
      recentTables.addRecent(fullPath, getNodeTitle(node))
      // Always navigate to regular table view when clicking a table in the TOC
      multiTable.setTables([])
      account.openPage('tables', {
        table: fullPath.replace(/\.cbt$/, ''),
      })
    } else if (node.type === 'MultiTable') {
      const multiTableId = node.meta?.find(meta => meta.key === MULTI_TABLE_ID_META_KEY)?.value || ''
      if (multiTableId) {
        // Find the multi-table data and get the first table
        const selectedMultiTable = multiTable.multitables?.find(mt => mt.id === multiTableId)
        const firstTable = selectedMultiTable?.config?.tables?.[0]
        const firstTablePath = firstTable
          ? `${firstTable.folderPath}/${firstTable.filename}`.replace(/\.cbt$/, '')
          : ''

        account.openPage('multitables', {
          multiTableId,
          table: firstTablePath,
        })
      }
    }
  }

  const onTableAdded = (node: GenNode) => {
    const fullPath = getNodeID(node)
    if(isMultiTableRoute) {
      const isIncluded = multiTable.isInMultiTable(fullPath)
      if(!isIncluded) {
        multiTable.setTables(prev => {
          return [...prev, {
            displayName: getNodeTitle(node),
            folderPath: node.value2 || '',
            filename: node.value1 || ''
          }]
        })
      }
      router.setParams({
        table: fullPath,
      })
    } else {
      const tables: MultiTableConfigTable[] = [
        {
          displayName: getNodeTitle(node),
          folderPath: node.value2 || '',
          filename: node.value1 || ''
        }
      ]

      if(fullPath.replace(/\.cbt$/, '') != table.replace(/\.cbt$/, '') && table) {
        tables.push({
          displayName: tableName?.replace(/\.cbt$/, '') || '',
          folderPath,
          filename: tableName || '',
        })
      }

      account.openPage('multitables', {
        multiTableId: multiTable.multiTable?.id || 'new',
        table: fullPath,
      })
      multiTable.setTables(tables)
    }
  }

  const onTableRemoved = (fullPath: string) => {
    const isCurrentTable = fullPath === table
    const newTables = multiTable.tables.filter(table => `${table.folderPath}/${table.filename}` !== fullPath)
    multiTable.setTables(newTables)

    if(isCurrentTable) {
      if(newTables.length > 0) {
        account.openPage('multitables', {
          multiTableId,
          table: `${newTables[0].folderPath}/${newTables[0].filename}`,
        })
      } else {
        account.openPage('tables', {
          table: fullPath.replace(/\.cbt$/, ''),
        })
      }
    }
  }

  const onNodeExpandToggle = (itemId: string, node: GenNode, isExpanded: boolean) => {
    setExpanded(prev => {
      if(isExpanded) {
        return [...prev, itemId]
      } else {
        return prev.filter(id => id !== itemId)
      }
    })
  }

  const handleDeleteTable = (node: GenNode) => {
    setPendingDelete({ type: 'table', data: node })
    setDeleteConfirmOpen(true)
  }

  const handleDeleteMultiTable = (multiTableId: string) => {
    setPendingDelete({ type: 'multitable', data: multiTableId })
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return

    setDeleteConfirmOpen(false)

    try {
      if (pendingDelete.type === 'table') {
        const node = pendingDelete.data as GenNode
        const fullPath = getNodeID(node)
        // Delete table via API
        await axios.delete(`${API_BASE_URL}/tables/${customer}/${job}/data?path=${encodeURIComponent(fullPath)}`)
        snackbar.success('Table deleted successfully')
        recentTables.removeRecent([fullPath])
        await reloadTree()
        // Navigate away if we're viewing the deleted table
        if (table === fullPath) {
          account.openPage('tables', { table: '' })
        }
      } else if (pendingDelete.type === 'multitable') {
        const multiTableId = pendingDelete.data as string
        await multiTable.deleteMultiTable(multiTableId)
        snackbar.success('Multi-table deleted successfully')
        await reloadTree()
        // Navigate to regular tables view
        account.openPage('tables', { table: table || '' })
      }
    } catch (err) {
      snackbar.error(`Failed to delete: ${(err as Error).message}`)
    } finally {
      setPendingDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false)
    setPendingDelete(null)
  }

  const handleClearFolderCache = async (node: GenNode) => {
    const folderPath = getNodeID(node)
    const sessionId = account.user?.session_info?.sessionId || ''
    try {
      const result = await clearFolderCache(sessionId, {
        customer: customer!,
        job: job!,
        folderPath,
      })
      setFolderCacheResult(result)
      setFolderCacheMode('clear')
      setFolderCacheDialogOpen(true)
    } catch (err) {
      snackbar.error(`Failed to clear folder cache: ${(err as Error).message}`)
    }
  }

  const handleRebuildFolderCache = async (node: GenNode) => {
    const folderPath = getNodeID(node)
    const sessionId = account.user?.session_info?.sessionId || ''
    // Gather all table paths under this folder from account.toc
    const tablePaths = flattenTree(account.toc || [])
      .filter(n => n.type === 'Table' && getNodeID(n).startsWith(folderPath + '/'))
      .map(n => getNodeID(n))

    if (tablePaths.length === 0) {
      snackbar.warning('No tables found under this folder')
      return
    }

    try {
      const result = await rebuildFolderCache(sessionId, {
        customer: customer!,
        job: job!,
        folderPath,
        tablePaths,
      })
      setFolderCacheResult(result)
      setFolderCacheMode('rebuild')
      setFolderCacheDialogOpen(true)
      snackbar.success(`Enqueued ${result.tablesEnqueued} tables for cache rebuild`)
    } catch (err) {
      snackbar.error(`Failed to rebuild folder cache: ${(err as Error).message}`)
    }
  }

  // The tree's name prompt: Rename, Duplicate and New folder (#1534). One
  // request at a time; each gets its own dialog (a key) so the field starts
  // from that request's name.
  const [nameRequest, setNameRequest] = useState<{ id: number; kind: 'rename' | 'duplicate' | 'copy' | 'new_folder'; node: GenNode } | null>(null)
  const nameRequestIdRef = useRef(0)
  const askName = (kind: 'rename' | 'duplicate' | 'copy' | 'new_folder', node: GenNode) => {
    nameRequestIdRef.current += 1
    setNameRequest({ id: nameRequestIdRef.current, kind, node })
  }

  // The changes behind Rename, Duplicate and New folder. The dialogs and the
  // page tools both run these, so both make the same change. Each reports
  // the outcome, and throws (after showing the error) when it fails.
  const userEmail = account.user?.session_info?.email || ''

  // A My Tables table or folder moved from `from` to `to` (blob paths): the
  // recent list follows it, and an open table moved with it stays open.
  const followMove = (from: string, to: string) => {
    recentTables.renameRecent(from, to)
    const strip = (p: string) => p.replace(/\.cbt$/i, '')
    const open = strip(table || '')
    const f = strip(from)
    if (!open) return
    if (open.toLowerCase() === f.toLowerCase()) account.openPage('tables', { table: strip(to) })
    else if (open.toLowerCase().startsWith(`${f.toLowerCase()}/`)) account.openPage('tables', { table: strip(to) + open.slice(f.length) })
  }

  const renameTocItem = async (node: GenNode, newName: string): Promise<string> => {
    const isFolder = node.type === 'Folder'
    const fullPath = getNodeID(node)
    const isMine = checkIsUserTable(isFolder ? node.value2 || '' : fullPath)
    try {
      if (isMine) {
        // My Tables: the server moves the blobs, so the tree follows. A
        // user.toc edit alone would leave the table under its old name.
        const res = isFolder
          ? await removal.handleRenameUserFolder(node, newName)
          : await removal.handleMoveUserTable(node, extractUserFolder(fullPath, userEmail), newName)
        if (res) followMove(res.from, res.to)
      } else {
        await tocApi.renameItem(customer!, job!, isFolder ? execFolderPath(node) : fullPath, newName, isFolder)
      }
      await reloadTree()
    } catch (err) {
      // The My Tables routes have already shown the server's reason.
      if (!isMine) snackbar.error(`Failed to rename: ${(err as Error).message}`)
      throw err
    }
    const done = `${isFolder ? 'Folder' : 'Table'} renamed to "${newName}"`
    snackbar.success(done)
    return done
  }

  const duplicateTocTable = async (node: GenNode, newName: string): Promise<string> => {
    const fullPath = getNodeID(node)
    const isMine = checkIsUserTable(fullPath)
    try {
      await axios.post(`${API_BASE_URL}/job/${customer}/${job}/toc/copy`, {
        sourcePath: fullPath + '.cbt',
        folder: isMine ? '' : getExecFolderFromPath(fullPath),
        name: newName,
        destination: isMine ? 'user' : 'exec',
      })
      await reloadTree()
    } catch (err) {
      // The server's message names the clash (409) or the Exec rule (403).
      snackbar.error(`Failed to duplicate: ${extractErrorMessage(err)}`)
      throw err
    }
    const done = `Table duplicated as "${newName}"`
    snackbar.success(done)
    return done
  }

  // "Make your own copy": anyone copies a table they can read into their own
  // My Tables (POST /toc/copy destination=user, #1748), then the copy opens so
  // they can change it. A name already there is a 409 that names it.
  const copyTableToMyTables = async (node: GenNode, name: string): Promise<string> => {
    const email = account.user?.session_info?.email || ''
    try {
      await axios.post(`${API_BASE_URL}/job/${customer}/${job}/toc/copy`, {
        sourcePath: getNodeID(node) + '.cbt',
        folder: '',
        name,
        destination: 'user',
      })
      await reloadTree()
    } catch (err) {
      snackbar.error(`Failed to copy: ${extractErrorMessage(err)}`)
      throw err
    }
    openTocTable(`Tables/User/${email}/${name}`)
    const done = `Copied "${getNodeTitle(node)}" to My Tables as "${name}"`
    snackbar.success(done)
    return done
  }

  // parent: My Tables (User), one of its folders, the Exec root (Section) or
  // an Exec folder. A My Tables folder lives in user.toc until a table is
  // saved into it.
  const createTocFolder = async (parent: GenNode, name: string): Promise<string> => {
    try {
      if (parent.type === 'User') await userTOC.createFolder(name)
      else if (parent.type === 'Folder' && checkIsUserTable(parent.value2 || '')) {
        // The parent's own path below Tables/User/<email>, e.g. 'Waves/Round 2'.
        const parentPath = extractUserFolder(`${getNodeID(parent)}/x`, userEmail)
        await userTOC.createFolder(name, parentPath)
      } else await tocApi.createFolder(customer!, job!, parent.type === 'Section' ? '' : execFolderPath(parent), name)
      await reloadTree()
    } catch (err) {
      snackbar.error(`Failed to create folder: ${(err as Error).message}`)
      throw err
    }
    const done = `Folder "${name}" created`
    snackbar.success(done)
    return done
  }

  const moveTocEntry = async (node: GenNode, direction: 'up' | 'down') => {
    try {
      if (node.type === 'Table') {
        // Sibling table names bootstrap user.toc on the first move
        const userSection = (account.toc || []).find(n => n.type === 'User')
        const allTableNames = (userSection?.children || [])
          .filter(c => c.type === 'Table')
          .map(c => c.value1 || '')
        await userTOC.moveEntry(node.value1 || '', direction, allTableNames)
      } else {
        await userTOC.moveEntry(node.value1 || '', direction)
      }
    } catch (err) {
      snackbar.error(`Failed to move: ${extractErrorMessage(err)}`)
    }
    await reloadTree()
  }

  // Move a My Tables table into one of its folders ('' = the top level). The
  // server moves the blob; a folder named new in the picker is made first.
  const moveUserTable = async (node: GenNode, destFolder: string) => {
    const known = collectUserFolders(userTOC.entries, '', 1).some(f => f.path === destFolder)
    if (destFolder && !known) {
      const parts = destFolder.split('/')
      try {
        await userTOC.createFolder(parts.pop() || '', parts.join('/'))
      } catch (err) {
        snackbar.error(`Failed to make the folder: ${extractErrorMessage(err)}`)
        return
      }
    }
    try {
      const res = await removal.handleMoveUserTable(node, destFolder)
      if (res) {
        followMove(res.from, res.to)
        snackbar.success(`Table moved to "${destFolder || 'My Tables'}"`)
      }
    } catch {
      // handleMoveUserTable has already shown the server's reason.
    }
    await reloadTree()
  }

  // Delete keeps its own confirmation and is never a page tool (Adam, 8 Oct).
  const handleDeleteNode = async (node: GenNode) => {
    const fullPath = getNodeID(node)
    if (node.type === 'Table') {
      // Any My Tables path, own or a colleague's, goes to the My Tables route:
      // it allows the owner or a Manager and keeps the owner's user.toc in
      // step. The Exec route refuses Tables/User/ paths.
      if (fullPath.toLowerCase().startsWith('tables/user/')) {
        handleDeleteTable(node)
        return
      }
    }
    // An Exec table, or a folder (My Tables or Exec): the server resolves
    // every table inside a folder, at any depth, and the confirm shows its
    // count and asks for the word "delete" (#1795).
    await removal.handleRequestDelete([node])
  }

  const handleRebuildAllCache = async (node: GenNode) => {
    const sid = account.user?.session_info?.sessionId || ''
    // The My Tables the menu was opened on: the user's own, or a colleague's
    // (whose root node is named by their email).
    const owner = isColleagueRoot(node) ? node.value1 || '' : account.user?.session_info?.email || ''
    const folderPath = `Tables/User/${owner}`
    const tablePaths = flattenTree(node.children || [])
      .filter(n => n.type === 'Table')
      .map(n => getNodeID(n))
    if (tablePaths.length === 0) {
      snackbar.warning('No tables found in My Tables')
      return
    }
    try {
      const result = await rebuildFolderCache(sid, {
        customer: customer!,
        job: job!,
        folderPath,
        tablePaths,
      })
      snackbar.success(`Enqueued ${result.tablesEnqueued} tables for cache rebuild`)
    } catch (err) {
      snackbar.error(`Failed to rebuild cache: ${(err as Error).message}`)
    }
  }

  // What each tree node's menu offers (tocNodeActions.tsx). The menu and the
  // page tools read the same list.
  const tocActionContext: TocActionContext = {
    isManager: !!isManager,
    // A Manager or a Realm admin, as the server's isAdmin on the cache routes.
    isAdmin: !!account.user?.role_permissions?.isRealmAdmin,
    isUserTable: checkIsUserTable,
    isInMultiTable: (path) => multiTable.isInMultiTable(path),
    hasUserFolders: userTOC.folders.length > 0,
    who: whoToAsk,
    on: {
      addToMultiTable: (node) => onTableAdded(node),
      removeFromMultiTable: (path) => onTableRemoved(path),
      context: (node) => handleNodeSettings(node),
      rename: (node) => askName('rename', node),
      moveUp: (node) => { void moveTocEntry(node, 'up') },
      moveDown: (node) => { void moveTocEntry(node, 'down') },
      move: (node) => {
        setPendingMoveNode(node)
        setMoveDialogOpen(true)
      },
      duplicate: (node) => askName('duplicate', node),
      makeCopy: (node) => askName('copy', node),
      delete: (node) => { void handleDeleteNode(node) },
      deleteMultiTable: (id) => handleDeleteMultiTable(id),
      newFolder: (parent) => askName('new_folder', parent),
      downloadFolderExcel: (node) => { void live().handleExportFolderXlsx(node) },
      regenerate: (node) => {
        if (!customer || !job) return
        account.openPage('batch-regenerate', {
          customer,
          job,
          sourceFolder: node.value2 ? `${node.value2}/${node.value1}` : (node.value1 || ''),
          sourceFolderName: node.value1 || '',
        })
      },
      clearFolderCache: (node) => { void handleClearFolderCache(node) },
      rebuildFolderCache: (node) => { void handleRebuildFolderCache(node) },
      rebuildAllCache: (node) => { void handleRebuildAllCache(node) },
    },
  }

  const getNodeActions = (node: GenNode) => {
    const actions = buildTocNodeActions(node, tocActionContext)
    if (actions.length === 0) return null
    return <MenuActionsButton actions={actions} ariaLabel={`More actions for ${getNodeTitle(node)}`} />
  }

  // Tree item ids are hierarchical (parent/child), as the Tree builds them.
  const expandTocNode = (target: GenNode) => {
    const targetId = getNodeID(target)
    const walk = (nodes: GenNode[], parentId: string, trail: string[]): string[] | null => {
      for (const node of nodes) {
        const baseID = node.id ? node.id.toString() : getNodeID(node)
        const uniqueID = parentId ? `${parentId}/${baseID}` : baseID
        if (node.type === target.type && getNodeID(node) === targetId) return [...trail, uniqueID]
        if (node.children?.length) {
          const found = walk(node.children, uniqueID, [...trail, uniqueID])
          if (found) return found
        }
      }
      return null
    }
    const ids = walk(multiTable.augmentedToc.nodes || [], '', [])
    if (ids) setExpanded(prev => Array.from(new Set([...prev, ...ids])))
  }

  const openTocTable = (tablePath: string) => {
    const node = flattenTree(account.toc || []).find(n => n.type === 'Table' && getNodeID(n) === tablePath)
    recentTables.addRecent(tablePath, node ? getNodeTitle(node) : tablePath.split('/').pop() || tablePath)
    multiTable.setTables([])
    account.openPage('tables', { table: tablePath.replace(/\.cbt$/, '') })
  }

  const handleDownloadXLSX = async () => {
    if (!customer || !job || !table) return

    // Preview: the server runs the unsaved spec and returns the workbook
    if (specEditor.previewSpec) {
      try {
        snackbar.info('Preparing Excel...')
        const urlVis = getURLBasedVisibilitySettings()
        await downloadSpecXlsx({
          customer,
          job,
          spec: specEditor.previewSpec,
          visibility: tableWrapperRef.current?.currentVisibility ?? { ...getDefaultTableViewSettings().visibility, ...urlVis },
          fileName: table.replace(/\.cbt$/i, '').split('/').pop() || 'table',
          tablePath: table,
        })
      } catch (err) {
        snackbar.error(`Excel download failed: ${(err as Error).message}`)
      }
      return
    }

    // Saved table: the /xlsx endpoint, with what the user is viewing
    try {
      snackbar.info('Preparing Excel...')
      const urlVis = getURLBasedVisibilitySettings()
      openSavedTableXlsx({
        customer,
        job,
        tablePath: table,
        // What the table shows (its own properties, saved view and URL), so this
        // matches the View table popup's workbook for the same table and filter.
        visibility: tableWrapperRef.current?.currentVisibility ?? { ...getDefaultTableViewSettings().visibility, ...urlVis },
        filter: effectiveFilter,
        token: (account.user && (account.user as any).token) || '',
      })
    } catch (err) {
      snackbar.error(`Excel download failed: ${(err as Error).message}`)
    }
  }

  const handleDownloadMultiTableXLSX = async () => {
    if (!customer || !job || !multiTableId || multiTableId === 'new') return

    try {
      snackbar.info('Preparing multi-table Excel...')

      // Get current visibility settings from URL
      const urlVis = getURLBasedVisibilitySettings()
      const defaults = getDefaultTableViewSettings().visibility
      const visibility = { ...defaults, ...urlVis }

      const params = new URLSearchParams({
        frequency: String(visibility.frequency),
        column_percent: String(visibility.column_percent),
        row_percent: String(visibility.row_percent),
        significance: String(visibility.significance),
      })

      // Include effective filter (URL filter takes precedence over saved config filter)
      if (effectiveFilter) {
        params.set('filter', effectiveFilter)
      }

      const token = (account.user && (account.user as any).token) || ''
      if (token) {
        params.set('token', token)
      }

      const url = `${API_BASE_URL}/multitables/${customer}/${job}/${multiTableId}/xlsx?${params.toString()}`

      const a = document.createElement('a')
      a.href = url
      a.rel = 'noopener'
      a.target = '_blank'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    } catch (err) {
      snackbar.error(`Excel download failed: ${(err as Error).message}`)
    }
  }

  const handleExportFolderXlsx = async (node: GenNode) => {
    if (!customer || !job) return
    // Build full folder path: value2 is the parent blob path, value1 is this folder's name
    const parentPath = node.value2 || ''
    const folderPath = parentPath ? `${parentPath}/${node.value1 || ''}` : (node.value1 || '')
    const folderName = node.value1 || 'Folder Export'
    try {
      snackbar.info('Preparing folder Excel...')
      const response = await axios.post(
        `${API_BASE_URL}/tables/${customer}/${job}/folder-xlsx`,
        { folderPath, reportName: folderName },
        { responseType: 'blob' },
      )
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${folderName}.xlsx`
      a.rel = 'noopener'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch (err) {
      // The response is a blob, so the server's own message has to be read
      // out of it — the axios message alone is "status code 500" (#1356).
      snackbar.error(`Folder Excel download failed: ${await blobErrorMessage(err)}`)
    }
  }

  const handleEdit = () => {
    if (!table && !autoCoderTable.isAutoCoderMode) return

    // Clear any existing draft for this table before editing
    if (table) {
      clearDraftForTable(table)
    }

    // Get current visibility from URL params (what user is currently viewing)
    const urlVis = getURLBasedVisibilitySettings()
    const defaults = getDefaultTableViewSettings().visibility
    // Merge URL settings with defaults to get complete visibility state
    const visibility = { ...defaults, ...urlVis }

    const editParams: Record<string, string> = {
      frequency: String(visibility.frequency),
      column_percent: String(visibility.column_percent),
      row_percent: String(visibility.row_percent),
      significance: String(visibility.significance),
    }

    // For AutoCoder mode, pass spec params back to EditSpec
    if (autoCoderTable.isAutoCoderMode && autoCoderTable.spec) {
      editParams.storageDriver = 'autocoder'
      editParams.acTop = autoCoderTable.spec.top
      editParams.acSide = autoCoderTable.spec.side
      if (autoCoderTable.spec.filter) editParams.acFilter = autoCoderTable.spec.filter
      if (autoCoderTable.spec.weight) editParams.acWeight = autoCoderTable.spec.weight
      if (Object.keys(autoCoderTable.spec.labels).length > 0) {
        editParams.acLabels = encodeURIComponent(JSON.stringify(autoCoderTable.spec.labels))
      }
      editParams.table = 'new'
    } else if (table) {
      editParams.table = table
    }

    account.openPage('editspec', editParams)
  }

  const handleQuickFilterSave = (filterString: string) => {
    router.setParams({ filter: filterString })
    setQuickFilterOpen(false)
    // In preview mode, clear stale preview data so the spec re-runs with the new filter
    if (isPreviewMode && specEditor.previewSpec) {
      const base = `${customer}/${job}/${(tableParam || 'new').replace(/\.cbt$/, '')}`
      try { window.sessionStorage.removeItem(`previewdata:${base}`) } catch {}
      specEditor.onLoadPreview?.()
    }
  }

  const handleClearFilter = () => {
    router.setParams({ filter: '' })
    // In preview mode, clear stale preview data so the spec re-runs without the filter
    if (isPreviewMode && specEditor.previewSpec) {
      const base = `${customer}/${job}/${(tableParam || 'new').replace(/\.cbt$/, '')}`
      try { window.sessionStorage.removeItem(`previewdata:${base}`) } catch {}
      specEditor.onLoadPreview?.()
    }
  }

  const handleOpenSettings = () => {
    // A temporary table (Edit Spec's unsaved result) shows its preview spec's
    // settings, not the editor's: for a new table the editor holds none.
    const previewSettings = isPreviewMode ? specEditor.previewSpec : null
    const dp = previewSettings?.displayProperties ?? specEditor.displayProperties
    const sp = previewSettings?.specProperties ?? specEditor.specProperties
    // A saved table shows the visibility its URL asks for, so sync the drawer
    // from what it is showing. A temporary table shows its preview spec's own
    // cells, already in dp (#1885).
    const vis = isPreviewMode ? undefined : tableWrapperRef.current?.currentVisibility
    const syncedDp = vis ? {
      ...dp,
      cells: {
        ...dp.cells,
        frequencies: vis.frequency ?? dp.cells?.frequencies ?? false,
        columnPercents: vis.column_percent ?? dp.cells?.columnPercents ?? true,
        rowPercents: vis.row_percent ?? dp.cells?.rowPercents ?? false,
      },
      significance: {
        ...dp.significance,
        visible: vis.significance ?? dp.significance?.visible ?? false,
      },
    } : dp
    specEditor.setDisplayProperties(syncedDp as typeof dp)
    specEditor.setSpecProperties(sp)

    settingsSnapshotRef.current = JSON.stringify({
      displayProperties: syncedDp,
      specProperties: sp,
    })
    if (openTableContext) void quickSettingsConfig.openConfigEntity(openTableContext)
    setSettingsDrawerOpen(true)
  }

  const handleCloseSettings = () => {
    // Revert to snapshot on cancel
    if (settingsSnapshotRef.current) {
      try {
        const snapshot = JSON.parse(settingsSnapshotRef.current)
        specEditor.setDisplayProperties(snapshot.displayProperties)
        specEditor.setSpecProperties(snapshot.specProperties)
      } catch {}
    }
    setSettingsDrawerOpen(false)
  }

  const pendingSaveTableRef = useRef(false)

  // When previewSpec becomes available and we have a pending save, open the dialog
  useEffect(() => {
    if (pendingSaveTableRef.current && specEditor.previewSpec) {
      pendingSaveTableRef.current = false
      specEditor.onSaveSpec()
    }
  }, [specEditor.previewSpec])

  const handleSaveTable = () => {
    // If preview spec already exists, open dialog immediately
    if (specEditor.previewSpec) {
      specEditor.onSaveSpec()
      return
    }
    // Build spec, store in sessionStorage, load into state
    if (customer && job) {
      const spec = specEditor.buildCarbonSpec()
      try {
        window.sessionStorage.setItem(specEditor.sessionStorageKeys.spec, JSON.stringify(spec))
      } catch {}
      pendingSaveTableRef.current = true
      specEditor.onLoadPreview?.()
    }
  }

  const handleApplySettings = () => {
    const snapshot = settingsSnapshotRef.current ? JSON.parse(settingsSnapshotRef.current) : null
    const dp = specEditor.displayProperties
    const snapshotDp = snapshot?.displayProperties

    // #1813: a temporary table (Edit Spec's unsaved result) is drawn from its
    // preview spec, not from the saved file, so the URL visibility params a
    // saved table reloads with never reach it. Run the preview spec again with
    // these settings, keeping its axes, filter and weight. The drawer opened on
    // the preview spec's own settings, so what it holds now is those settings
    // plus only what the user changed.
    if (isPreviewMode && specEditor.previewSpec) {
      const current = JSON.stringify({ displayProperties: dp, specProperties: specEditor.specProperties })
      if (current !== settingsSnapshotRef.current) {
        const { displayProperties, specProperties } = specEditor.buildCarbonSpec()
        rerunPreviewSpec({ ...specEditor.previewSpec, displayProperties, specProperties })
      }
      setSettingsDrawerOpen(false)
      return
    }

    // Non-visibility settings (decimals, significance config, sort, titles,
    // spec properties) need a run, which makes a temporary table. Its preview
    // spec then carries the visibility too, the drawer's as it stands, so
    // nothing is written to the URL (#1885).
    const currentNoVis = JSON.stringify({ ...dp, cells: undefined, significance: { ...dp.significance, visible: undefined } })
    const snapshotNoVis = JSON.stringify({ ...snapshotDp, cells: undefined, significance: { ...snapshotDp?.significance, visible: undefined } })
    const specChanged = JSON.stringify(specEditor.specProperties) !== JSON.stringify(snapshot?.specProperties)
    if ((currentNoVis !== snapshotNoVis || specChanged) && customer && job) {
      rerunPreviewSpec(specEditor.buildCarbonSpec())
      setSettingsDrawerOpen(false)
      return
    }

    // Only visibility changed: a saved table keeps it in the URL.
    const newVisibility: Record<string, boolean> = {}
    const cells = dp.cells
    const snapshotCells = snapshotDp?.cells
    if (cells?.frequencies !== snapshotCells?.frequencies) newVisibility.frequency = cells?.frequencies ?? false
    if (cells?.columnPercents !== snapshotCells?.columnPercents) newVisibility.column_percent = cells?.columnPercents ?? true
    if (cells?.rowPercents !== snapshotCells?.rowPercents) newVisibility.row_percent = cells?.rowPercents ?? false
    if (dp.significance?.visible !== snapshotDp?.significance?.visible) newVisibility.significance = dp.significance?.visible ?? false

    // Apply visibility changes as URL params
    if (Object.keys(newVisibility).length > 0) {
      const tableVis = tableWrapperRef.current?.currentVisibility
      const merged = { ...tableVis, ...newVisibility } as Partial<TableVisibilitySettings>
      const urlParams = convertVisibilitySettingsToURL(merged)
      const allVisParams = getAllVisibilityParamNames('')
      const currentParams = { ...router.params }
      allVisParams.forEach(p => { delete currentParams[p] })
      router.navigate(router.name, { ...currentParams, ...urlParams })
    }

    setSettingsDrawerOpen(false)
  }

  useEffect(() => {
    const flattenedNodes = flattenTree(multiTable.augmentedToc.nodes || [])

    // Top-level folders plus every node the TOC flags expanded, as
    // hierarchical Tree ids — shared with the dashboard pickers.
    const expandedFromToc = getInitialExpandedIds(multiTable.augmentedToc.nodes || [])

    const ancestorIds = (() => {
      if(!table) return [] as string[]
      // Normalize path separators (URL may have backslashes, nodeId uses forward slashes)
      const normalizedTable = table.replace(/\\/g, '/')

      // Find ancestor nodes and sort by path length (root to leaf order)
      const ancestorNodes = flattenedNodes
        .filter(node => {
          if(node.type !== 'Folder' && node.type !== 'User' && node.type !== 'Section' && node.type !== 'Colleague') return false
          const nodeId = getNodeID(node)
          return normalizedTable.startsWith(nodeId)
        })
        .sort((a, b) => (getNodeID(a).length) - (getNodeID(b).length))

      // Build hierarchical IDs (tree uses format: parentId/parentId/.../nodeId)
      const hierarchicalIds: string[] = []
      let parentPath = ''
      for (const node of ancestorNodes) {
        const baseId = node.id?.toString() || ''
        const hierarchicalId = parentPath ? `${parentPath}/${baseId}` : baseId
        hierarchicalIds.push(hierarchicalId)
        parentPath = hierarchicalId
      }

      return hierarchicalIds
    })()

    setExpanded(prev => {
      const newExpanded = new Set(prev)

      // Add TOC-expanded nodes
      expandedFromToc.forEach(id => {
        if (id) newExpanded.add(id)
      })

      // Add ancestor nodes
      ancestorIds.forEach(id => {
        if (id) newExpanded.add(id)
      })

      if (isMultiTableRoute) {
        if (multiTable.augmentedToc.userNodeId) {
          newExpanded.add(multiTable.augmentedToc.userNodeId)
        }
        if (multiTable.augmentedToc.multiTableFolderId) {
          newExpanded.add(multiTable.augmentedToc.multiTableFolderId)
        }
      }

      return Array.from(newExpanded)
    })
  }, [
    account.toc,
    multiTable.multitables,
    multiTable.augmentedToc.nodes,
    table,
    isMultiTableRoute,
  ])

  useEffect(() => {
    if(!multiTableId) {
      multiTable.setTables([])
    }
  }, [multiTableId])

  // Read the tree and user.toc every time the page mounts or the job
  // changes: the agent (pt toc save / delete), another tab or a colleague may
  // have changed tables since the tree was last read.
  useEffect(() => {
    if (customer && job) void reloadTree()
  }, [customer, job])

  useEffect(() => {
    const loadJobConfig = async () => {
      if (!customer || !job) {
        setJobMergedConfig(null)
        return
      }

      try {
        const merged = await configApi.getMergedConfig(customer, job, {
          config_type: ConfigType.Job,
          customer,
          job
        })
        setJobMergedConfig(merged)
      } catch (err) {
        console.error('Error loading job merged config:', err)
        setJobMergedConfig(null)
      }
    }

    loadJobConfig()
  }, [customer, job])

  // The variable tree and a variable's codes, for find_variable_codes. The
  // tree is fetched once per job.
  const vartreeRef = useRef<{ key: string; nodes: Promise<GenNode[]> } | null>(null)
  const loadVariableTree = () => {
    const key = `${customer}/${job}`
    if (vartreeRef.current?.key !== key) {
      const nodes = axios
        .get<{ variableTree?: GenNode[] }>(`${API_BASE_URL}/job/${customer}/${job}/vartree`)
        .then(res => res.data.variableTree || [])
      nodes.catch(() => {
        if (vartreeRef.current?.key === key) vartreeRef.current = null
      })
      vartreeRef.current = { key, nodes }
    }
    return vartreeRef.current.nodes
  }
  const loadVariableCodes = async (variable: string) => {
    const res = await axios.get<GenNode[]>(`${API_BASE_URL}/job/${customer}/${job}/vartree/codes?variable_id=${encodeURIComponent(variable)}`)
    return res.data || []
  }
  // The same check the Quick Filter dialog runs before it saves.
  const validateFilter = async (expression: string): Promise<string | null> => {
    try {
      await axios.post(`${API_BASE_URL}/tables/${customer}/${job}/validate/expression`, { syntax: expression })
      return null
    } catch (err) {
      return extractErrorMessage(err)
    }
  }

  liveRef.current = {
    table: table || '',
    isAutoCoder: autoCoderTable.isAutoCoderMode,
    isPreviewMode,
    hasUnsavedViewSettings,
    tableData: (autoCoderTable.data || specEditor.previewData || effectivePlatinumData) as PlatinumData | undefined,
    filter: effectiveFilter || '',
    pageContext: agentPageContext,
    applyVisibilitySettings,
    applyChartType,
    handleEdit,
    newTable: () => {
      clearDraftForTable('new')
      account.openPage('editspec')
    },
    toggleChat,
    discardChanges: () => {
      if (isPreviewMode) specEditor.onClearPreview()
      tableWrapperRef.current?.discardViewSettings()
    },
    saveViewSettings: () => tableWrapperRef.current?.saveViewSettings(),
    handleSaveTable,
    handleDownloadXLSX,
    star: () => recentTables.toggleStar(table),
    close: () => account.openPage('tables', { table: '' }),
    view: () => tableWrapperRef.current,
    validateFilter,
    handleQuickFilterSave,
    handleClearFilter,
    openQuickFilter: () => setQuickFilterOpen(true),
    variableTree: loadVariableTree,
    variableCodes: loadVariableCodes,
    settingsOpen: settingsDrawerOpen,
    displayProperties: specEditor.displayProperties,
    specProperties: specEditor.specProperties,
    handleOpenSettings,
    writeSettings: (dp, sp) => {
      specEditor.setDisplayProperties(dp)
      specEditor.setSpecProperties(sp)
    },
    handleApplySettings,
    handleCloseSettings,
    tocNodes: multiTable.augmentedToc.nodes || [],
    // The tools run only what the menu allows, and refuse with its reason.
    tocActionsFor: (node) => buildTocNodeActions(node, tocActionContext).filter(a => !a.disabled).map(a => a.key),
    tocRefusalFor: (node, key) => tocActionRefusal(node, key, tocActionContext),
    renameTocItem,
    duplicateTocTable,
    copyTableToMyTables,
    createTocFolder,
    openTocTable,
    expandTocNode,
    handleExportFolderXlsx,
  }

  const sidebar = !account.loading && account.job && (
    <>
    {removal.selectMode && (
      <TocSelectBar count={removal.selectedCount} busy={removal.busy} onDelete={() => { void removal.handleDeleteTicked() }} />
    )}
    <Tree
      searchEnabled={true}
      getCheckboxState={(node) => removal.getCheckboxState(node, (n) => {
        const del = buildTocNodeActions(n, tocActionContext).find(a => a.key === 'delete')
        return del && { disabled: del.disabled, reason: del.reason }
      })}
      onNodeCheckedToggle={removal.handleNodeCheckedToggle}
      nodes={multiTable.augmentedToc.nodes}
      expanded={expanded}
      selected={selectedID}
      getNodeActions={getNodeActions}
      onNodeSelectToggle={onNodeSelectToggle}
      onNodeExpandToggle={onNodeExpandToggle}
    />
    </>
  )

  // Regular table mode rendering
  return (
    <SideBarPage
      title="Table of Contents"
      headerContent={<TocSelectToggle selectMode={removal.selectMode} onToggle={removal.handleToggleSelectMode} />}
      sidebar={ sidebar }
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}>

        {isMultiTableRoute && multiTable.multiTable && (
          <MultiTableHeader
            data={multiTable.multiTable}
            tables={multiTable.tables || []}
            activeTable={table}
            allMultiTables={multiTable.multitables || []}
            onOpenTable={(path) => {
              // Ensure path is normalized (without .cbt) before setting URL param
              // useTablePath will add it back if needed
              const normalizedPath = normalizeTablePath(path, true)
              router.setParams({
                table: normalizedPath,
              })
            }}
            onRemoveTable={(index) => {
              const table = multiTable.tables[index]
              onTableRemoved(`${table.folderPath}/${table.filename}`)
            }}
            onUpdateFilter={(filter) => {
              router.setParams({
                filter,
              })
            }}
            onSave={ async (data) => {
              await multiTable.saveMultiTable(data)
              snackbar.success('Multi-table saved successfully')
            }}
            onDelete={ async () => {
              await multiTable.deleteMultiTable(multiTableId)
              snackbar.success('Multi-table deleted successfully')
              account.openPage('tables', {
                table: table,
              })
            }}
            onCancel={() => {
              account.openPage('tables', {
                table: table,
              })
            }}
            onOpenAnalysis={() => setAnalysisDrawerOpen(true)}
            onDownloadXLSX={handleDownloadMultiTableXLSX}
          />
        )}

        <PageHeader
          title={autoCoderTable.isAutoCoderMode ? 'AutoCoder Table' : (tableTitle || 'Tables')}
          subtitle={
            cbtSide && !autoCoderTable.isAutoCoderMode
              ? cbtSide
              : (!tableTitle && !autoCoderTable.isAutoCoderMode ? 'Select a table from the sidebar to get started' : undefined)
          }
          actions={
            <TablesHeaderActions
              hasTable={!!table || autoCoderTable.isAutoCoderMode}
              isAutoCoder={autoCoderTable.isAutoCoderMode}
              isPreviewMode={isPreviewMode}
              onEdit={handleEdit}
              onNewTable={() => {
                clearDraftForTable('new')
                account.openPage('editspec')
              }}
              onInsights={toggleChat}
            />
          }
        />
        
        <Paper elevation={0} sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 0,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}>
          {(isPreviewMode || hasUnsavedViewSettings) && !autoCoderTable.isAutoCoderMode && (
            <TablesPreviewBanner
              isPreviewMode={isPreviewMode}
              saving={specEditor.savingPreview}
              onDiscard={() => {
                if (isPreviewMode) specEditor.onClearPreview()
                tableWrapperRef.current?.discardViewSettings()
              }}
              onSaveSettings={() => tableWrapperRef.current?.saveViewSettings()}
              onSaveTable={handleSaveTable}
            />
          )}

          {/* AutoCoder loading state */}
          {autoCoderTable.isAutoCoderMode && autoCoderTable.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography>Loading AutoCoder table...</Typography>
            </Box>
          )}

          {/* AutoCoder error state */}
          {autoCoderTable.isAutoCoderMode && autoCoderTable.error && (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to load AutoCoder table: {autoCoderTable.error}
            </Alert>
          )}

          {/* AutoCoder table rendering */}
          {autoCoderTable.isAutoCoderMode && !autoCoderTable.loading && !autoCoderTable.error && autoCoderTable.data && (
            <PlatinumTableWrapper
              key={`autocoder-${JSON.stringify(autoCoderTable.visibility)}`}
              customer={customer || ''}
              job={job || ''}
              tablePath={undefined}
              filter={undefined}
              data={autoCoderTable.data}
              onDataLoaded={undefined}
            />
          )}

          {/* Spec preview: loading state */}
          {isPreviewMode && (specRunning || needsRun) && !specRunError && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
              <CircularProgress size={24} sx={{ mr: 2 }} />
              <Typography>Running spec...</Typography>
            </Box>
          )}

          {/* Spec preview: error state */}
          {isPreviewMode && specRunError && (
            <Alert severity="error" sx={{ m: 2 }}>
              Failed to run spec: {specRunError}
            </Alert>
          )}

          {/* Regular table rendering (includes spec preview when data is available) */}
          {
            !autoCoderTable.isAutoCoderMode && tableParam && !(isPreviewMode && (specRunning || needsRun)) && (
              <PlatinumTableWrapper
                ref={tableWrapperRef}
                customer={customer || ''}
                job={job || ''}
                tablePath={tableParam}
                filter={effectiveFilter}
                data={isPreviewMode ? (specEditor.previewData || undefined) : undefined}
                onDataLoaded={setEffectivePlatinumData}
                onVisibilityOverride={isPreviewMode ? handleSpecVisibilityOverride : undefined}
                onQuickFilter={() => setQuickFilterOpen(true)}
                onClearFilter={handleClearFilter}
                onSettings={handleOpenSettings}
                onUnsavedChangesChange={setHasUnsavedViewSettings}
                onDownloadXlsx={handleDownloadXLSX}
              />
            )
          }
          {
            !autoCoderTable.isAutoCoderMode && !tableParam && (
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  p: 4,
                }}
              >
                {recentTables.tables.length > 0 ? (
                  <Box sx={{ maxWidth: 400 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.7rem' }}>
                      Recent Tables
                    </Typography>
                    {recentTables.tables.slice(0, 10).map(t => (
                      <Box
                        key={t.path}
                        onClick={() => account.openPage('tables', { table: t.path.replace(/\.cbt$/, '') })}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.25,
                          borderRadius: 1,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                      >
                        <TableChartIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2" sx={{ flex: 1, color: 'text.primary' }}>
                          {t.name}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            recentTables.toggleStar(t.path)
                          }}
                          sx={{ p: 0.25, color: t.starred ? 'secondary.main' : 'text.secondary', opacity: t.starred ? 1 : 0.3, '&:hover': { opacity: 1 } }}
                        >
                          {t.starred ? <StarIcon sx={{ fontSize: 14 }} /> : <StarBorderIcon sx={{ fontSize: 14 }} />}
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Select a table from the sidebar to get started
                    </Typography>
                  </Box>
                )}
              </Box>
            )
          }
        </Paper>
      </Box>

      {/* ConfigEditor Modal */}
      <ConfigEditor
        open={configEditorOpen}
        onClose={closeConfigEditor}
        entity={configEntity}
        mergedConfig={mergedConfig}
        onSave={handleSaveConfig}
        onEditEntity={openConfigEntity}
        loading={loadingConfig}
        saving={savingConfig}
        handleNodeSettings={handleNodeSettings}
      />

      {/* Insights: the Tables page helper, the agent with this page's tools. Persistent, so the page stays usable while it works on it.
          A press on the page closes it (on mouse down, so a text selection dragged out of the pane does not); a press in a dialog or menu, or on the Insights button, does not (paneClickAway). */}
      <ClickAwayListener
        mouseEvent={helperOpen ? 'onMouseDown' : false}
        touchEvent={helperOpen ? 'onTouchStart' : false}
        onClickAway={(e) => { if (shouldCloseOnClickAway(e.target)) setHelperOpen(false) }}
      >
      <Drawer
        anchor="right"
        variant="persistent"
        open={helperOpen}
        onClose={() => setHelperOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: INSIGHTS_DRAWER_WIDTH.width,
            minWidth: INSIGHTS_DRAWER_WIDTH.minWidth,
            maxWidth: INSIGHTS_DRAWER_WIDTH.maxWidth,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          },
        }}
      >
        {helperMounted && (
          <PageAgentPane
            pageKey="tables"
            customer={customer || ''}
            job={job || ''}
            open={helperOpen}
            resumeKey={`${customer}/${job}/${table}`}
            onClose={() => setHelperOpen(false)}
            getPageContext={() => [live().pageContext(), starter.contextBlock()].filter(Boolean).join('\n')}
            getTools={() => voiceToolsRef.current ?? []}
            sessionOptions={pageAgentHost.sessionOptions}
            plugins={pageAgentHost.plugins}
            // The Tables pane's model is the server's: no picker below agent access.
            models={fullAgent ? pageAgentHost.models : undefined}
            introRows={starter.rows}
            introLoading={starter.loading}
            composerDisabledReason={helperComposerReason}
            profile={helperProfile}
            fullChatDisabledReason={fullChatReason}
          />
        )}
      </Drawer>
      </ClickAwayListener>

      {/* Name prompt for the tree's Rename, Duplicate, Make your own copy and New folder (#1534) */}
      {nameRequest && (
        <TocNameDialog
          key={nameRequest.id}
          open
          title={
            nameRequest.kind === 'rename'
              ? `Rename ${nameRequest.node.type === 'Table' ? 'table' : 'folder'}`
              : nameRequest.kind === 'duplicate'
                ? 'Duplicate table'
                : nameRequest.kind === 'copy'
                  ? 'Copy to My Tables'
                  : nameRequest.node.type === 'Folder' ? 'New subfolder' : 'New folder'
          }
          label={nameRequest.kind === 'rename' ? 'New name' : nameRequest.kind === 'duplicate' || nameRequest.kind === 'copy' ? 'Name for copy' : 'Folder name'}
          initialValue={
            nameRequest.kind === 'rename' || nameRequest.kind === 'copy'
              ? nameRequest.node.value1 || ''
              : nameRequest.kind === 'duplicate' ? `${nameRequest.node.value1 || ''}_copy` : ''
          }
          submitLabel={nameRequest.kind === 'rename' ? 'Rename' : nameRequest.kind === 'duplicate' ? 'Duplicate' : nameRequest.kind === 'copy' ? 'Copy' : 'Create'}
          requireChange={nameRequest.kind === 'rename'}
          onSubmit={async (name) => {
            const { kind, node } = nameRequest
            if (kind === 'rename') await renameTocItem(node, name)
            else if (kind === 'duplicate') await duplicateTocTable(node, name)
            else if (kind === 'copy') await copyTableToMyTables(node, name)
            else await createTocFolder(node, name)
          }}
          onClose={() => setNameRequest(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmWindow
        open={deleteConfirmOpen}
        title={pendingDelete?.type === 'table' ? 'Delete Table' : 'Delete Multi-Table'}
        message={
          pendingDelete?.type === 'table'
            ? `Are you sure you want to delete the table "${getNodeTitle(pendingDelete.data)}"? This action cannot be undone.`
            : `Are you sure you want to delete this multi-table? This action cannot be undone.`
        }
        confirmWord="delete"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {/* Folder and Select-mode delete: the server's count (#1795) */}
      <DeleteConfirmWindow
        open={!!removal.pending}
        title={removal.pending?.title}
        message={removal.pending?.message}
        confirmWord={removal.pending?.confirmWord ?? ''}
        onConfirm={() => { void removal.handleConfirm() }}
        onCancel={removal.handleCancel}
      />
      <TableDeleteProgress progress={removal.progress} />

      {/* Move to Folder Dialog */}
      <FolderPickerDialog
        open={moveDialogOpen}
        onClose={() => { setMoveDialogOpen(false); setPendingMoveNode(null) }}
        title="Move to Folder"
        tocNodes={account.toc || []}
        userEmail={account.user?.session_info?.email || ''}
        isManager={!!isManager}
        execOnly={!!pendingMoveNode && !checkIsUserTable(getNodeID(pendingMoveNode))}
        userOnly={!!pendingMoveNode && checkIsUserTable(getNodeID(pendingMoveNode))}
        userFolders={userTOC.entries}
        onSelect={async (folderPath, destination) => {
          if (!pendingMoveNode || !customer || !job) return
          const node = pendingMoveNode
          const nodePath = getNodeID(node)
          setPendingMoveNode(null)
          if (checkIsUserTable(nodePath)) {
            await moveUserTable(node, folderPath)
            return
          }
          try {
            // Exec table — move via API
            await tocApi.moveTable(customer, job, nodePath, folderPath)
            await reloadTree()
            snackbar.success(`Table moved to "${folderPath || 'root'}"`)
          } catch (err) {
            snackbar.error(`Failed to move: ${(err as Error).message}`)
          }
        }}
      />

      {/* Multi-Table Analysis Drawer */}
      {isMultiTableRoute && multiTableId && customer && job && (
        <MultiTableAnalysisDrawer
          open={analysisDrawerOpen}
          onClose={() => setAnalysisDrawerOpen(false)}
          customer={customer}
          job={job}
          multiTableId={multiTableId}
          multiTableName={multiTable.multiTable?.config?.name}
          personas={personas}
        />
      )}

      {/* Folder Cache Result Dialog */}
      <FolderCacheResultDialog
        open={folderCacheDialogOpen}
        onClose={() => setFolderCacheDialogOpen(false)}
        result={folderCacheResult}
        mode={folderCacheMode}
      />

      {/* Settings Drawer */}
      <SpecSettingsDrawer
        open={settingsDrawerOpen}
        onClose={handleCloseSettings}
        displayProperties={specEditor.displayProperties}
        specProperties={specEditor.specProperties}
        onDisplayPropertiesChange={specEditor.setDisplayProperties}
        onSpecPropertiesChange={specEditor.setSpecProperties}
        mergedConfig={openTableContextLoaded ? quickSettingsConfig.mergedConfig : null}
        loadingConfig={quickSettingsConfig.loadingConfig}
        savingConfig={quickSettingsConfig.savingConfig}
        onSaveConfig={openTableContext ? (data) => quickSettingsConfig.saveConfigFor(openTableContext, data) : undefined}
        onApply={handleApplySettings}
      />

      {/* Quick Filter Modal */}
      <FilterEditorModal
        open={quickFilterOpen}
        onClose={() => setQuickFilterOpen(false)}
        onSave={handleQuickFilterSave}
        currentFilter={effectiveFilter || ''}
      />

      {/* Save Table Dialog */}
      <SaveTableDialog
        open={specEditor.saveDialogOpen}
        originalPath={specEditor.saveDialogTablePath}
        userEmail={account.user?.session_info?.email || ''}
        onSave={specEditor.onConfirmSave}
        onCancel={specEditor.onCancelSave}
        saving={specEditor.savingPreview}
        isManager={account.user?.role_permissions?.isManager}
        userFolders={userTOC.entries}
        execFolders={(() => {
          // Extract Exec folder names from TOC for helper text
          const folders: string[] = []
          const walk = (nodes: GenNode[]) => {
            for (const n of nodes) {
              if (n.type === 'Folder') folders.push(n.value1 || '')
              if (n.children?.length) walk(n.children)
            }
          }
          const execRoot = (account.toc || []).find(n => n.type === 'Section' || n.value2?.includes('Exec'))
          if (execRoot?.children) walk(execRoot.children)
          return folders
        })()}
      />
    </SideBarPage>
  )
}

export default Tables
